import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { CvEditorSession } from "../types/cv-editor.types";
import { AuthContext } from "../../../../../../../contexts/AuthContext";
import { updateLocale } from "../../../../../../../services/users.service";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";

interface ResumeOrStartOverStepProps {
  session: CvEditorSession;
  onContinue: () => void;
  onStartOver: () => void;
  isLoading: boolean;
}

export const ResumeOrStartOverStep: React.FC<ResumeOrStartOverStepProps> = ({
  session,
  onContinue,
  onStartOver,
  isLoading,
}) => {
  const { t, i18n } = useTranslation("cvEditor");
  const { user } = useContext(AuthContext); // Get user from context to check cookie settings

  const sessionLocale = session?.locale; // e.g., 'en-US', 'de-DE'
  const currentUiLang = i18n.language; // e.g., 'en', 'de'

  const localeMismatch =
    sessionLocale && !currentUiLang.startsWith(sessionLocale.split("-")[0]);

  const getLanguageName = (locale: string) => {
    switch (locale) {
      case "de":
        return "German";
      case "en-US":
        return "English";
      default:
        return locale;
    }
  };

  const getFormattedLang = (lang: string) => {
    if (lang.startsWith("de")) return "de";
    return "en-US";
  };

  const updateLocaleMutation = useMutation(
    ["localeUpdate"],
    (mutationArgs: { locale: "de" | "en-US" }) =>
      updateLocale({ locale: mutationArgs.locale })
  );

  const handleContinueClick = () => {
    if (localeMismatch) {
      const targetLang = sessionLocale.startsWith("de") ? "de" : "en";

      i18n.changeLanguage(targetLang);
      localStorage.setItem("language", targetLang);

      if (user?.cookieSettings?.preferencesCookiesAccepted) {
        updateLocaleMutation.mutate({
          locale: getFormattedLang(sessionLocale) as "de" | "en-US",
        });
      }
    }
    onContinue();
  };

  return (
    <div className="p-6 flex flex-col h-full">
      <div className="flex-grow overflow-y-auto pr-2 -mr-2 flex flex-col items-center justify-center text-center">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          {t("resumeStep.title")}
        </h3>
        <p className="text-gray-600 mb-6 max-w-md">
          {t("resumeStep.description")}
        </p>

        {localeMismatch && sessionLocale && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 max-w-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <WarningAmberIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3 text-left">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t("resumeStep.warning.title")}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    {t("resumeStep.warning.description", {
                      locale: getLanguageName(sessionLocale),
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4 w-full max-w-xs">
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            onClick={handleContinueClick}
            // The main loading state should apply here
            isLoading={isLoading || updateLocaleMutation.isLoading}
          >
            {t("resumeStep.continueButton")}
          </Button>
          <Button
            className="w-full"
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={onStartOver}
            isLoading={isLoading}
          >
            {t("resumeStep.startOverButton")}
          </Button>
        </div>
      </div>
    </div>
  );
};
